import { type ReactNode } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useTemporaryBooleanState } from '@/hooks';
import { CheckSmallIcon, LinkIcon, SquareStackIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
export const SHARE_ROW_CLASS_NAME = 'flex min-h-[56px] w-full items-center gap-xs px-md py-sm text-left transition-colors hover:bg-neutral';

/** 피그마 PlaySharePopover 의 묶음 목록(링크 줄 · 클래스 줄). */
export function ShareActionList({
  obscured,
  children
}: {
  obscured?: boolean;
  children: ReactNode;
}) {
  return <div className={cn('flex w-full shrink-0 flex-col divide-y divide-alternative overflow-hidden rounded-md border border-alternative', obscured && 'blur-[4px]')} data-sentry-component="ShareActionList" data-sentry-source-file="ShareActionList.tsx">
      {children}
    </div>;
}

/** 피그마 ShareLinkRow: 줄을 누르면 링크를 복사한다. */
export function ShareLinkRow({
  url,
  onCopied
}: {
  url: string;
  /** 복사 시도 결과를 넘긴다. 공유 수단 계측은 모달이 소유한다. */
  onCopied?: (succeeded: boolean) => void;
}) {
  const {
    t
  } = useTranslation(['common']);
  const [isLinkCopied, setIsLinkCopied] = useTemporaryBooleanState(false, 2000);
  return <CopyToClipboard text={url} onCopy={(_text, succeeded) => {
    logger.track('share_link_copy', {
      link: url
    });
    // 클립보드 권한 거부·API 실패도 onCopy 로 들어온다. 성공으로 뭉치면
    // 이벤트의 result 가 항상 success 라 필드가 무의미해진다.
    onCopied?.(succeeded);
    if (succeeded) setIsLinkCopied(true);
  }} data-sentry-element="CopyToClipboard" data-sentry-component="ShareLinkRow" data-sentry-source-file="ShareActionList.tsx">
      <button type="button" aria-label={t('shareModal.copyLink.button')} className={SHARE_ROW_CLASS_NAME}>
        <LinkIcon size="md" className="shrink-0 text-blue-500" data-sentry-element="LinkIcon" data-sentry-source-file="ShareActionList.tsx" />
        <span className="min-w-0 flex-1 truncate text-body-lg-medium text-default">
          {url}
        </span>
        {isLinkCopied ? <CheckSmallIcon size="sm" fill={ICON_COLOR.strong} /> : <SquareStackIcon size="sm" fill={ICON_COLOR.alternative} />}
      </button>
    </CopyToClipboard>;
}